import { useOutletContext } from 'react-router';
import Typography from '@mui/material/Typography';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import type { AssetDetailFragment } from '../AssetDetailPage';
import { displayBoolean, displayDate, displayDecimal, displayInteger, displayText } from '../../../components/ui';

/**
 * Asset Details (General) tab — read-only two-column layout matching FAIM I's
 * AssetDetails.razor:
 *
 * Left column (plain field rows):
 *   1. Asset References
 *   2. Brand & Model
 *   3. Acquisition & Status
 *
 * Right column (collapsible accordion):
 *   - Service Maintenance
 *   - Attributes, Usage & Telemetry
 */
export function AssetGeneralTab() {
  const asset = useOutletContext<AssetDetailFragment>();

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 py-6" data-testid="asset-general-tab">
      {/* ── Left column ─────────────────────────────────────────────────── */}
      <div className="flex flex-col gap-6">
        {/* Section 1 — Asset References */}
        <Section title="Asset References">
          <FieldRow label="Serial Number" value={asset.serialNumber} />
          <FieldRow label="PIN" value={asset.pin} />
          <FieldRow label="Local Asset Number" value={asset.localAssetNumber} />
          <FieldRow label="SAP Order Number" value={asset.sapOrderNumber} />
          <FieldRow label="Equipment Number" value={asset.equipmentNumber} />
          <FieldRow label="Statistical Internal Order" value={asset.statisticalInternalOrder} />
          <FieldRow label="ERP Asset Number" value={asset.erpAssetNumber} />
        </Section>

        {/* Section 2 — Brand & Model */}
        <Section title="Brand & Model">
          <FieldRow label="Brand" value={asset.brandName} />
          <FieldRow label="Model" value={asset.modelName} />
          <FieldRow label="Cluster" value={asset.clusterName} />
          {asset.detailedClusterName && (
            <FieldRow label="Detailed Cluster" value={asset.detailedClusterName} />
          )}
          <FieldRow label="Year of Manufacture" value={displayInteger(asset.yearOfManufacture)} />
          {asset.isWaterFuelTruckApplicable && (
            <FieldRow label="Water / Fuel Truck" value={displayBoolean(asset.isWaterFuelTruck)} />
          )}
        </Section>

        {/* Section 3 — Acquisition & Status */}
        <Section title="Acquisition & Status">
          <FieldRow label="Form of Finance" value={asset.formOfFinanceName} />
          <FieldRow label="Joint Venture" value={displayBoolean(asset.jointVenture)} />
          {asset.jointVenture && asset.jvInterest != null && (
            <FieldRow label="JV Interest (%)" value={displayInteger(asset.jvInterest)} />
          )}
          <FieldRow label="Fleet Responsibility" value={displayBoolean(asset.fleetResponsibility)} />
          <FieldRow
            label="Commission Date"
            value={displayDate(asset.commissionDate == null ? null : String(asset.commissionDate))}
          />
          <FieldRow label="Project Number" value={asset.projectNumber} />
          <FieldRow label="Warranty" value={asset.warrantyName} />
          <FieldRow label="Include in Plan" value={displayBoolean(asset.includeInPlan)} />
        </Section>
      </div>

      {/* ── Right column ────────────────────────────────────────────────── */}
      <div className="flex flex-col gap-2">
        {/* Accordion 1 — Service Maintenance */}
        <Accordion defaultExpanded disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">Service Maintenance</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {asset.serviceContractUnknown ? (
              <Typography variant="body2" color="text.secondary">
                Service contract is unknown.
              </Typography>
            ) : (
              <>
                <FieldRow label="Service Provider Type" value={asset.serviceProviderTypeName} />
                <FieldRow label="Service Dealer" value={asset.serviceDealerName} />
                <FieldRow label="Service Company" value={asset.serviceCompanyName} />
                <FieldRow
                  label="Service Start Date"
                  value={displayDate(asset.serviceStartDate == null ? null : String(asset.serviceStartDate))}
                />
                <FieldRow
                  label="Service End Date"
                  value={displayDate(asset.serviceEndDate == null ? null : String(asset.serviceEndDate))}
                />
                <FieldRow label="Service Type" value={asset.serviceTypeName} />
                <FieldRow label="Total MR Hours" value={displayInteger(asset.totalMRHours)} />
                {asset.serviceCostEnabled && (
                  <FieldRow label="Service Cost Details" value={asset.serviceCostDetails} />
                )}
              </>
            )}
          </AccordionDetails>
        </Accordion>

        {/* Accordion 2 — Attributes, Usage & Telemetry */}
        <Accordion defaultExpanded disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">Attributes, Usage & Telemetry</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <FieldRow label="Utilization" value={asset.utilizationName} />
            <FieldRow label="Payload (m³)" value={displayDecimal(asset.payloadInM3 == null ? null : Number(asset.payloadInM3))} />
            <FieldRow label="Tyre Size" value={asset.tyreSize} />
            <FieldRow label="Machine Name" value={asset.machineName} />
            <FieldRow label="Engine Emission Standard" value={asset.engineEmissionStandardName} />
            <FieldRow label="Telematics Support" value={displayBoolean(asset.telematicsSupport)} />
            {asset.telematicsSupport && (
              <>
                <FieldRow label="Telematics Type" value={asset.telematicsTypeName} />
                <FieldRow label="Telematics Code" value={asset.telematicsCode} />
              </>
            )}
          </AccordionDetails>
        </Accordion>
      </div>
    </div>
  );
}

// ── Internal helpers ──────────────────────────────────────────────────────────

interface SectionProps {
  title: string;
  children: React.ReactNode;
}

function Section({ title, children }: SectionProps) {
  return (
    <div>
      <Typography
        variant="overline"
        color="text.secondary"
        component="h3"
        sx={{ display: 'block', mb: 1 }}
      >
        {title}
      </Typography>
      <div className="flex flex-col gap-1">{children}</div>
    </div>
  );
}

interface FieldRowProps {
  label: string;
  value: string | null | undefined;
}

function FieldRow({ label, value }: FieldRowProps) {
  return (
    <div className="grid grid-cols-[1fr_2fr] gap-2 py-1 border-b border-divider last:border-0">
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2">{displayText(value)}</Typography>
    </div>
  );
}
